/**
 * Katalog: GoBD-Verfahrensdokumentation Friseurbetrieb
 * Dokument-ID: PEPAPERS-COMPLIANCE-GOBD-01
 */

export const GOBD_DOC_ID = 'PEPAPERS-COMPLIANCE-GOBD-01';

export const GOBD_DOC_TITLE = 'GOBD-VERFAHRENSDOKUMENTATION FÜR FRISEURBETRIEBE';

export const GOBD_DOC_SUBTITLE =
  'Organisations- und Verfahrensanweisung zur ordnungsmäßigen Kassen- und Buchführung gem. §§ 146, 146b, 147 AO, KassenSichV & GoBD';

export const GOBD_LEGAL_BASIS_LINE =
  'Rechtsgrundlagen: §§ 146, 146a, 146b, 147 AO, KassenSichV, GoBD (BMF), ggf. § 3 Abs. 13-15 UStG (Gutscheine)';

/** Presets Kassensystem — „Sonstiges“ = Freitext-Override im Formular */
export const GOBD_KASSENSYSTEM_PRESETS = [
  'Phorest',
  'Shore',
  'Studiolution',
  'Klick-Kasse',
  'Sonstiges',
] as const;

/** Presets Kartenterminal — „Sonstiges“ = Freitext-Override im Formular */
export const GOBD_TERMINAL_PRESETS = [
  'SumUp',
  'Ingenico',
  'Zettle',
  'Bank-Terminal',
  'Sonstiges',
] as const;

export type GobdTseTyp = 'cloud' | 'hardware';
export type GobdZBonRhythmus = 'taeglich' | 'schichtbezogen';
export type GobdGutscheinTyp = 'einzweck' | 'mehrzweck';

export interface GobdFormData {
  salonName: string;
  inhaberName: string;
  kassenSystemName: string;
  softwareVersion: string;
  tseTyp: GobdTseTyp | '';
  tseSeriennummer: string;
  zBonRhythmus: GobdZBonRhythmus | '';
  wechselgeldGrundbestand: number;
  barZahlungAktiv: boolean;
  unbarZahlungTerminal: string;
  gutscheinTypen: GobdGutscheinTyp[];
  notfallProzedere: string;
  datumErstellung: string;
  versionsNummer: string;
}

export interface GobdArchivePayload {
  formData: GobdFormData;
  documentId: typeof GOBD_DOC_ID;
  generatedHtml?: string;
}

export const GOBD_DEFAULT_NOTFALL =
  'Händische Quittungsblöcke mit fortlaufender Nummerierung bei Strom-/Systemausfall. Lückenlose Nachbuchung im Kassensystem unverzüglich nach Wiederherstellung inkl. TSE-Signierung und Verklammerung mit dem manuellen Eigenbeleg.';

export const GOBD_SECTION_TITLES = {
  s1: '1. Allgemeine Angaben & Salonstammdaten',
  s2: '2. Elektronisches Aufzeichnungssystem & TSE (§ 146a AO)',
  s3: '3. Kassenführung, Z-Bon-Rhythmus & Wechselgeld-Zählung',
  s4: '4. Unbare Zahlungen & Gutscheinbehandlung',
  s5: '5. Ausfall- & Notfallkonzept (Kassen-Nachschau-Sicherheit)',
  s6: '6. Aufbewahrungsfristen (10 Jahre) & Änderungshistorie',
} as const;

export const GOBD_SECTION_4_HINT =
  'Einzweck- vs. Mehrzweck-Gutscheine gem. § 3 Abs. 13–15 UStG: Einzweckgutscheine betreffen eine konkret bestimmte Leistung (Ort/Leistung bereits fest); Mehrzweckgutscheine lassen Leistung/Ort noch offen und werden erst bei Einlösung umsatzsteuerlich behandelt.';

export const GOBD_SECTION_6_TEXT =
  'Aufbewahrungsfrist: Die Verfahrensdokumentation, Kassendaten, Z-Bons, TSE-Daten und Belege werden revisionssicher für mindestens 10 Jahre aufbewahrt (§ 147 AO). Änderungen an dieser Dokumentation werden mit neuer Versionsnummer und Datum nachvollziehbar festgehalten (Änderungshistorie).';

/** Dezenter Seitenkopf für Druckseite 2 */
export const GOBD_PAGE2_BAND_LABEL = 'Seite 2 von 2';

export const GOBD_CONFIRMATION_TEXT =
  'Hiermit bestätige ich als Betriebsinhaber/in, dass die vorstehenden Angaben zur Kassenführung, TSE und den betrieblichen Verfahren dem tatsächlichen Ablauf in meinem Friseurbetrieb entsprechen. Mir ist bekannt, dass unrichtige oder unvollständige Angaben bei einer Kassennachschau / Betriebsprüfung nachteilige Folgen haben können.';

export function formatGobdTseTyp(v: GobdTseTyp | ''): string {
  if (v === 'cloud') return 'Cloud-TSE';
  if (v === 'hardware') return 'Hardware-TSE';
  return '—';
}

export function formatGobdZBon(v: GobdZBonRhythmus | ''): string {
  if (v === 'taeglich') return 'Täglich (Tagesabschluss / Z-Bon)';
  if (v === 'schichtbezogen') return 'Schichtbezogen (Z-Bon je Schicht)';
  return '—';
}

export function formatGobdGutscheine(types: GobdGutscheinTyp[]): string {
  if (!types.length) return 'Keine Gutscheine / nicht einschlägig';
  const labels: string[] = [];
  if (types.includes('einzweck')) labels.push('Einzweck-Gutscheine');
  if (types.includes('mehrzweck')) labels.push('Mehrzweck-Gutscheine');
  return labels.join(' · ');
}

export function formatGobdEuro(n: number): string {
  const v = Number.isFinite(n) ? n : 0;
  return v.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const todayIso = () => new Date().toISOString().split('T')[0];

export function getDefaultGobdFormData(
  salon?: Partial<Pick<GobdFormData, 'salonName' | 'inhaberName'>>
): GobdFormData {
  return {
    salonName: salon?.salonName || '',
    inhaberName: salon?.inhaberName || '',
    kassenSystemName: '',
    softwareVersion: '',
    tseTyp: '',
    tseSeriennummer: '',
    zBonRhythmus: 'taeglich',
    wechselgeldGrundbestand: 0,
    barZahlungAktiv: true,
    unbarZahlungTerminal: '',
    gutscheinTypen: [],
    notfallProzedere: GOBD_DEFAULT_NOTFALL,
    datumErstellung: todayIso(),
    versionsNummer: '1.0',
  };
}
